import {
  sha256CanonicalJson,
  sha256DocumentTemplateText,
  validateDocumentTemplateInput,
  validateDocumentTemplateManifest,
  type CogoDocumentTemplateManifest,
} from "./document-template-runtime";

export type CogoDocumentModelProposalKind =
  | "template-authoring"
  | "semantic-synthesis"
  | "template-selection"
  | "slot-filling"
  | "repair";

export interface CogoDocumentModelTelemetry {
  provider: string;
  model: string;
  servedModelVersion: string | null;
  providerRequestId: string | null;
  tokenUsage: {
    input: number;
    output: number;
    total: number;
  } | null;
  costUsd: number | null;
  latencyMs: number | null;
}

export interface CogoDocumentModelAdapterResult
  extends Partial<CogoDocumentModelTelemetry> {
  text: string;
  provider: string;
  model: string;
}

export interface CogoDocumentModelAdapter {
  invoke(input: {
    prompt: string;
    responseJsonSchema: unknown;
  }): Promise<CogoDocumentModelAdapterResult>;
}

export interface CogoDocumentTemplateModelProposal {
  schemaVersion: "cogo.document-template-model-proposal.v1";
  proposalId: string;
  proposalKind: CogoDocumentModelProposalKind;
  status: "proposed";
  createdAt: string;
  templateId: string;
  templateVersion: string;
  templateHash: string;
  sourceRef: string;
  sourceHash: string;
  semanticInput: Record<string, unknown>;
  semanticInputHash: string;
  modelInvocationReceiptId: string;
  requiresHumanReview: true;
  canonicalAuthority: false;
  finalWriterAllowed: false;
}

export interface CogoDocumentModelInvocationReceipt {
  schemaVersion: "cogo.document-model-invocation-receipt.v1";
  receiptId: string;
  generatedAt: string;
  invocationMode: "live-api" | "interactive-session" | "offline-replay";
  proposalKind: CogoDocumentModelProposalKind;
  provider: string;
  model: string;
  servedModelVersion: string | null;
  promptRef: string;
  promptHash: string;
  inputContextRefs: string[];
  inputContextHash: string;
  policyRef: string;
  policyHash: string;
  outputRef: string;
  outputArtifactHash: string;
  status: "completed" | "incomplete" | "failed";
  telemetryCompleteness: "complete" | "partial" | "unavailable";
  unavailableTelemetry: Array<
    | "served-model-version"
    | "provider-request-id"
    | "token-usage"
    | "cost"
    | "latency"
  >;
  providerRequestId: string | null;
  tokenUsage: CogoDocumentModelTelemetry["tokenUsage"];
  costUsd: number | null;
  latencyMs: number | null;
  reasoningTraceStored: false;
  rawModelOutputStored: false;
  finalWriterAllowed: false;
  canonicalAuthority: false;
  requiresHumanReview: true;
  claimBoundary: string;
}

export interface RunCogoDocumentTemplateModelProposalResult {
  proposal: CogoDocumentTemplateModelProposal;
  receipt: CogoDocumentModelInvocationReceipt;
  prompt: string;
}

export const COGO_DOCUMENT_MODEL_ROLE_POLICY = {
  schemaVersion: "cogo.document-model-role-policy.v1",
  allowedProposalKinds: [
    "template-authoring",
    "semantic-synthesis",
    "template-selection",
    "slot-filling",
    "repair",
  ],
  finalWriter: "deterministic-template-compiler",
  modelFinalWriterAllowed: false,
  modelCanonicalAuthority: false,
  automatedPromotionAllowed: false,
  promotionAuthority: "deterministic-gates-plus-human-review",
  reasoningTraceStored: false,
  rawModelOutputStored: false,
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSha256(value: string): boolean {
  return /^sha256:[a-f0-9]{64}$/.test(value);
}

export function isCogoDocumentModelDateTime(value: string): boolean {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/,
  );
  if (!match) return false;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const offsetHour = Number(match[7] ?? 0);
  const offsetMinute = Number(match[8] ?? 0);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    leapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1]! &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 59 &&
    offsetHour <= 23 &&
    offsetMinute <= 59 &&
    Number.isFinite(Date.parse(value))
  );
}

function stripJsonFence(text: string): string {
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  return (fenced?.[1] ?? trimmed).trim();
}

export function parseCogoDocumentModelSemanticInput(text: string): unknown {
  const payload = stripJsonFence(text);
  if (!payload.startsWith("{") || !payload.endsWith("}")) {
    throw new Error("Model proposal must be one JSON object with no prose.");
  }
  try {
    return JSON.parse(payload) as unknown;
  } catch (error) {
    throw new Error(
      `Model proposal is not valid JSON: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
}

export function buildCogoDocumentTemplateModelPrompt(input: {
  proposalKind: CogoDocumentModelProposalKind;
  sourceText: string;
  sourceRef: string;
  template: CogoDocumentTemplateManifest;
  inputSchema: unknown;
}): string {
  return [
    "You are a bounded Coogo document semantic-input proposer.",
    `Proposal kind: ${input.proposalKind}.`,
    "Return exactly one JSON object accepted by INPUT_SCHEMA. Do not return Markdown or explanations.",
    "Every evidenceRef value must start with source:// and be traceable to SOURCE.",
    "You may synthesize and prioritize meaning, but you may not change the template, write a final document, claim canonical authority, or promote the result.",
    "TEMPLATE:",
    JSON.stringify(input.template),
    "INPUT_SCHEMA:",
    JSON.stringify(input.inputSchema),
    `SOURCE_REF: ${input.sourceRef}`,
    "SOURCE:",
    input.sourceText,
  ].join("\n");
}

function normalizeTelemetry(
  result: CogoDocumentModelAdapterResult,
): CogoDocumentModelTelemetry {
  return {
    provider: result.provider,
    model: result.model,
    servedModelVersion: result.servedModelVersion ?? null,
    providerRequestId: result.providerRequestId ?? null,
    tokenUsage: result.tokenUsage ?? null,
    costUsd: result.costUsd ?? null,
    latencyMs: result.latencyMs ?? null,
  };
}

function unavailableTelemetry(
  telemetry: CogoDocumentModelTelemetry,
): CogoDocumentModelInvocationReceipt["unavailableTelemetry"] {
  return [
    ...(telemetry.servedModelVersion === null
      ? (["served-model-version"] as const)
      : []),
    ...(telemetry.providerRequestId === null
      ? (["provider-request-id"] as const)
      : []),
    ...(telemetry.tokenUsage === null ? (["token-usage"] as const) : []),
    ...(telemetry.costUsd === null ? (["cost"] as const) : []),
    ...(telemetry.latencyMs === null ? (["latency"] as const) : []),
  ];
}

export async function runCogoDocumentTemplateModelProposal(input: {
  adapter: CogoDocumentModelAdapter;
  proposalId: string;
  proposalKind?: CogoDocumentModelProposalKind;
  createdAt: string;
  sourceText: string;
  sourceRef: string;
  sourceHash: string;
  template: unknown;
  templateHash?: string;
  templateRef: string;
  inputSchema: unknown;
  inputSchemaRef: string;
  promptRef: string;
  policyRef: string;
  outputRef: string;
}): Promise<RunCogoDocumentTemplateModelProposalResult> {
  const templateErrors = validateDocumentTemplateManifest(input.template);
  if (templateErrors.length > 0) {
    throw new Error(`Invalid document template: ${templateErrors.join(" ")}`);
  }
  if (!input.proposalId.trim()) throw new Error("proposalId is required.");
  if (!isSha256(input.sourceHash)) throw new Error("sourceHash must be SHA-256.");
  if (!isCogoDocumentModelDateTime(input.createdAt)) {
    throw new Error("createdAt must be an ISO date-time.");
  }
  const proposalKind = input.proposalKind ?? "semantic-synthesis";
  const template = structuredClone(input.template) as CogoDocumentTemplateManifest;
  const templateHash = input.templateHash ?? (await sha256CanonicalJson(template));
  if (!isSha256(templateHash)) throw new Error("templateHash must be SHA-256.");
  const prompt = buildCogoDocumentTemplateModelPrompt({
    proposalKind,
    sourceText: input.sourceText,
    sourceRef: input.sourceRef,
    template,
    inputSchema: input.inputSchema,
  });
  const promptHash = await sha256DocumentTemplateText(prompt);
  const context = {
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    templateRef: input.templateRef,
    templateHash,
    inputSchemaRef: input.inputSchemaRef,
    inputSchemaHash: await sha256CanonicalJson(input.inputSchema),
  };
  const invoked = await input.adapter.invoke({
    prompt,
    responseJsonSchema: input.inputSchema,
  });
  const semanticInput = parseCogoDocumentModelSemanticInput(invoked.text);
  if (!isRecord(semanticInput)) {
    throw new Error("Model semantic input must be a JSON object.");
  }
  const inputErrors = validateDocumentTemplateInput(
    input.inputSchema,
    semanticInput,
  );
  if (inputErrors.length > 0) {
    throw new Error(
      `Model semantic input failed schema validation: ${inputErrors.join(" ")}`,
    );
  }
  const semanticInputHash = await sha256CanonicalJson(semanticInput);
  const receiptId = `${input.proposalId}.invocation`;
  const telemetry = normalizeTelemetry(invoked);
  const unavailable = unavailableTelemetry(telemetry);
  const proposal: CogoDocumentTemplateModelProposal = {
    schemaVersion: "cogo.document-template-model-proposal.v1",
    proposalId: input.proposalId,
    proposalKind,
    status: "proposed",
    createdAt: input.createdAt,
    templateId: template.templateId,
    templateVersion: template.templateVersion,
    templateHash,
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    semanticInput,
    semanticInputHash,
    modelInvocationReceiptId: receiptId,
    requiresHumanReview: true,
    canonicalAuthority: false,
    finalWriterAllowed: false,
  };
  const receipt: CogoDocumentModelInvocationReceipt = {
    schemaVersion: "cogo.document-model-invocation-receipt.v1",
    receiptId,
    generatedAt: input.createdAt,
    invocationMode: "live-api",
    proposalKind,
    provider: telemetry.provider,
    model: telemetry.model,
    servedModelVersion: telemetry.servedModelVersion,
    promptRef: input.promptRef,
    promptHash,
    inputContextRefs: [
      input.sourceRef,
      input.templateRef,
      input.inputSchemaRef,
    ],
    inputContextHash: await sha256CanonicalJson(context),
    policyRef: input.policyRef,
    policyHash: await sha256CanonicalJson(COGO_DOCUMENT_MODEL_ROLE_POLICY),
    outputRef: input.outputRef,
    outputArtifactHash: semanticInputHash,
    status: "completed",
    telemetryCompleteness: unavailable.length === 0 ? "complete" : "partial",
    unavailableTelemetry: unavailable,
    providerRequestId: telemetry.providerRequestId,
    tokenUsage: telemetry.tokenUsage,
    costUsd: telemetry.costUsd,
    latencyMs: telemetry.latencyMs,
    reasoningTraceStored: false,
    rawModelOutputStored: false,
    finalWriterAllowed: false,
    canonicalAuthority: false,
    requiresHumanReview: true,
    claimBoundary:
      "This receipt proves a typed model proposal and records only available invocation telemetry. The model did not write a final artifact, become canonical authority, or promote its output.",
  };
  return { proposal, receipt, prompt };
}
